import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import { sanitizeHtml } from '../lib/sanitize';
import { TopBar } from '../components/TopBar';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { CursorSpotlight } from '../components/CursorSpotlight';
import { getAllPosts, getPostBySlug } from '../lib/data';
import type { Post, WritingTeaser } from '../lib/types';

// `breaks: true` — posts are written in the admin textarea, where a single
// newline means a line break, not a paragraph continuation. With breaks off,
// blocks like "Stack: / Time: / Cost:" collapse into one run-on line.
marked.setOptions({ gfm: true, breaks: true });

export function EssayPage() {
  const { slug = '' } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [siblings, setSiblings] = useState<{
    prev: WritingTeaser | null;
    next: WritingTeaser | null;
  }>({ prev: null, next: null });

  useEffect(() => {
    setLoaded(false);
    setPost(null);
    window.scrollTo(0, 0);
    (async () => {
      const [p, all] = await Promise.all([getPostBySlug(slug), getAllPosts()]);
      setPost(p);
      setLoaded(true);
      // Chronological navigation: `all` is sorted newest-first, so the next
      // essay (more recent) sits BEFORE in the array, previous (older) AFTER.
      const idx = all.findIndex((w) => w.slug === slug);
      if (idx !== -1) {
        setSiblings({
          next: idx > 0 ? all[idx - 1] : null,
          prev: idx < all.length - 1 ? all[idx + 1] : null,
        });
      }
    })();
  }, [slug]);

  const html = useHtml(post);
  const wordCount = post ? countWords(post.body_html ?? post.body_md ?? '') : 0;

  return (
    <div
      style={{
        background: 'var(--paper)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-mono)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      <CursorSpotlight />
      <TopBar />
      <Nav />
      <article
        className="section"
        style={{ padding: '56px 28px', minHeight: '60vh', maxWidth: 780, margin: '0 auto' }}
      >
        {loaded && !post && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 20 }}>
              § · Not found
            </div>
            <h1 className="serif italic" style={{ fontSize: 48, margin: 0, marginBottom: 16 }}>
              No essay here.
            </h1>
            <Link to="/essays" style={{ color: 'var(--accent)' }}>
              ← all essays
            </Link>
          </div>
        )}
        {post && (
          <>
            <div className="eyebrow" style={{ marginBottom: 12 }}>
              {post.date} · {post.minutes} min · {wordCount.toLocaleString()} words
            </div>
            <h1
              className="serif"
              style={{
                fontSize: 54,
                lineHeight: 1.05,
                letterSpacing: -1,
                margin: 0,
                marginBottom: 12,
              }}
            >
              {post.title}
            </h1>
            {post.kicker && (
              <div
                className="serif italic"
                style={{ fontSize: 20, opacity: 0.7, marginBottom: 36 }}
              >
                {post.kicker}
              </div>
            )}
            <div
              className="serif essay-body"
              style={{ fontSize: 'var(--fs-body-serif)', lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: html }}
            />

            <nav
              aria-label="Essay navigation"
              style={{
                marginTop: 56,
                borderTop: '1px solid var(--ink)',
                paddingTop: 20,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
              }}
            >
              <div>
                {siblings.prev && (
                  <Link
                    to={`/essays/${siblings.prev.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <div
                      className="eyebrow"
                      style={{ marginBottom: 4, opacity: 0.5 }}
                    >
                      ← Earlier
                    </div>
                    <div className="serif" style={{ fontSize: 18, lineHeight: 1.2 }}>
                      {siblings.prev.title}
                    </div>
                    <div className="admin-status" style={{ opacity: 0.55, marginTop: 2 }}>
                      {siblings.prev.date}
                    </div>
                  </Link>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {siblings.next && (
                  <Link
                    to={`/essays/${siblings.next.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <div
                      className="eyebrow"
                      style={{ marginBottom: 4, opacity: 0.5 }}
                    >
                      Later →
                    </div>
                    <div className="serif" style={{ fontSize: 18, lineHeight: 1.2 }}>
                      {siblings.next.title}
                    </div>
                    <div className="admin-status" style={{ opacity: 0.55, marginTop: 2 }}>
                      {siblings.next.date}
                    </div>
                  </Link>
                )}
              </div>
            </nav>
            <div style={{ marginTop: 24 }}>
              <Link
                to="/essays"
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  textDecoration: 'none',
                  borderBottom: '1px solid var(--ink)',
                  paddingBottom: 2,
                }}
              >
                ← all essays
              </Link>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}

function countWords(src: string): number {
  const text = src.replace(/<[^>]+>/g, ' ');
  return text.split(/\s+/).filter(Boolean).length;
}

function useHtml(post: Post | null): string {
  const [html, setHtml] = useState('');
  useEffect(() => {
    if (!post) return setHtml('');
    const src = post.body_html ?? post.body_md ?? '';
    const rendered = post.body_html
      ? src
      : (marked.parse(src, { async: false }) as string);
    setHtml(sanitizeHtml(rendered));
  }, [post]);
  return html;
}
